import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

interface Props {
  text: string;
  stagger?: number;
  delay?: number;
  className?: string;
}

/**
 * WordsPullUp — per-character rise + fade entrance.
 * Falls back to static text when the document isn't visible (preview
 * panes, background tabs) so the wordmark is never stuck off-screen.
 */
export default function WordsPullUp({ text, stagger = 0.06, delay = 0, className = '' }: Props) {
  const [canAnimate, setCanAnimate] = useState(false);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (document.visibilityState !== 'visible') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    setCanAnimate(true);
  }, []);

  if (!canAnimate) return <span className={className}>{text}</span>;

  const words = text.split(' ');
  let i = 0;

  return (
    <span className={className} aria-label={text}>
      {words.map((word, wi) => (
        <span key={wi} className="inline-block overflow-hidden align-bottom pb-[0.08em] leading-none">
          {[...word].map((ch, ci) => {
            const charDelay = delay + i++ * stagger;
            return (
              <motion.span
                key={ci}
                aria-hidden
                initial={{ y: '110%', opacity: 0 }}
                animate={{ y: '0%', opacity: 1 }}
                transition={{ duration: 0.9, delay: charDelay, ease: [0.16, 1, 0.3, 1] }}
                className="inline-block"
              >
                {ch}
              </motion.span>
            );
          })}
          {wi < words.length - 1 && <span>&nbsp;</span>}
        </span>
      ))}
    </span>
  );
}
